import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Info } from "@phosphor-icons/react";

const STATUS_COLORS = {
  PENDING: "bg-stone-100 text-stone-600", ELIGIBLE: "bg-blue-100 text-blue-700",
  PROCESSING: "bg-amber-100 text-amber-700", PAID: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-700", ON_HOLD: "bg-orange-100 text-orange-700", CANCELLED: "bg-stone-200 text-stone-500",
};
const FILTERS = ["", "PENDING", "ELIGIBLE", "PROCESSING", "PAID", "FAILED", "ON_HOLD"];

export default function AdminPayouts() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [payDlg, setPayDlg] = useState(null);
  const [holdDlg, setHoldDlg] = useState(null);
  const [form, setForm] = useState({ reference: "", method: "UPI", date: "", note: "" });
  const [holdReason, setHoldReason] = useState("");

  const load = useCallback(() => {
    const params = filter ? { status: filter } : {};
    api.get("/admin/payouts", { params }).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load payouts"));
  }, [filter]);
  useEffect(() => { load(); setSelected([]); }, [load]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const eligibleSelected = (data?.payouts || []).filter((p) => selected.includes(p.id) && p.status === "ELIGIBLE");
  const batchTotal = eligibleSelected.reduce((a, p) => a + (p.net_amount || 0), 0);

  const createBatch = () => {
    api.post("/admin/payouts/batch", { payout_ids: eligibleSelected.map((p) => p.id) })
      .then((r) => { toast.success(`Batch ${r.data.batch_ref}: ${r.data.count} payouts, ${formatINR(r.data.total)}`); setSelected([]); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Batch failed"));
  };

  const markPaid = () => {
    if (!form.reference || !form.method) { toast.error("Reference and method are required"); return; }
    api.post(`/admin/payouts/${payDlg.id}/mark-paid`, form)
      .then(() => { toast.success("Payout marked paid"); setPayDlg(null); setForm({ reference: "", method: "UPI", date: "", note: "" }); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  };

  const doHold = () => {
    if (!holdReason.trim()) { toast.error("Reason required"); return; }
    api.post(`/admin/payouts/${holdDlg.id}/hold`, { reason: holdReason })
      .then(() => { toast.success("Payout on hold"); setHoldDlg(null); setHoldReason(""); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  };
  const action = (id, verb) => api.post(`/admin/payouts/${id}/${verb}`, {}).then(() => { toast.success("Updated"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));
  const disburse = (id) => api.post(`/admin/payouts/${id}/disburse`, {}).then(() => { toast.success("Disbursement initiated via RazorpayX"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed"));

  if (!data) return <DashboardLayout nav={adminNav} title="Admin"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;
  const S = data.summary;

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex items-center gap-2">
        <Wallet size={26} className="text-primary" />
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Owner Payouts</h1>
      </div>
      {data.provider === "manual" && (
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="payout-manual-banner">
          <Info size={18} /> Manual Payout Mode — no funds move automatically. Mark payouts paid after settling via NEFT/UPI/Bank Transfer.
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="payout-summary">
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">Eligible</p><p className="mt-1 font-heading text-xl font-bold text-blue-700">{formatINR(S.ELIGIBLE)}</p></div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">Pending</p><p className="mt-1 font-heading text-xl font-bold text-stone-700">{formatINR(S.PENDING + S.PROCESSING)}</p></div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">On Hold</p><p className="mt-1 font-heading text-xl font-bold text-orange-600">{formatINR(S.ON_HOLD)}</p></div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">Paid</p><p className="mt-1 font-heading text-xl font-bold text-green-600">{formatINR(S.PAID)}</p></div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`payout-filter-${f || "all"}`} onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${filter === f ? "bg-primary text-white" : "border border-stone-200 text-stone-600 hover:bg-accent"}`}>{f || "All"}</button>
        ))}
        {eligibleSelected.length > 0 && (
          <Button data-testid="payout-batch-btn" className="ml-auto rounded-full" onClick={createBatch}>
            Create batch · {eligibleSelected.length} · {formatINR(batchTotal)}
          </Button>
        )}
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        <table className="w-full text-sm" data-testid="payouts-table">
          <thead className="bg-stone-50 text-left text-xs text-stone-500"><tr>
            <th className="p-3"></th><th className="p-3">Payout</th><th className="p-3">Owner</th><th className="p-3">Booking</th>
            <th className="p-3">Event</th><th className="p-3 text-right">Customer Paid</th><th className="p-3 text-right">Commission</th>
            <th className="p-3 text-right">Net Payout</th><th className="p-3">Status</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {data.payouts.length === 0 ? <tr><td colSpan={10} className="p-6 text-center text-stone-400">No payouts yet.</td></tr> :
              data.payouts.map((p) => (
                <tr key={p.id} className="border-t border-stone-100" data-testid={`payout-row-${p.id}`}>
                  <td className="p-3">{p.status === "ELIGIBLE" && <input type="checkbox" data-testid={`payout-check-${p.id}`} checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />}</td>
                  <td className="p-3 font-medium text-stone-800">{p.payout_ref}</td>
                  <td className="p-3 text-stone-600">{p.owner_name}</td>
                  <td className="p-3 text-stone-500">{p.booking_ref}</td>
                  <td className="p-3 text-stone-500">{p.event_date}</td>
                  <td className="p-3 text-right">{formatINR(p.customer_payment)}</td>
                  <td className="p-3 text-right text-primary">{formatINR(p.commission)}</td>
                  <td className="p-3 text-right font-semibold">{formatINR(p.net_amount)}</td>
                  <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[p.status] || "bg-stone-100"}`}>{p.status}</span>
                    {p.status === "ON_HOLD" && p.hold_reason && <p className="mt-1 text-[10px] text-orange-600">{p.hold_reason}</p>}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {["ELIGIBLE", "PROCESSING", "FAILED"].includes(p.status) && <button data-testid={`payout-pay-${p.id}`} onClick={() => setPayDlg(p)} className="rounded-md bg-green-600 px-2 py-1 text-xs text-white">Mark Paid</button>}
                      {p.status === "ELIGIBLE" && data.provider === "razorpayx" && <button data-testid={`payout-disburse-${p.id}`} onClick={() => disburse(p.id)} className="rounded-md bg-primary px-2 py-1 text-xs text-white">Disburse (RazorpayX)</button>}
                      {p.status !== "PAID" && p.status !== "ON_HOLD" && <button data-testid={`payout-hold-${p.id}`} onClick={() => setHoldDlg(p)} className="rounded-md border border-orange-300 px-2 py-1 text-xs text-orange-700">Hold</button>}
                      {p.status === "ON_HOLD" && <button data-testid={`payout-release-${p.id}`} onClick={() => action(p.id, "release")} className="rounded-md border border-blue-300 px-2 py-1 text-xs text-blue-700">Release</button>}
                      {p.status === "PROCESSING" && <button data-testid={`payout-fail-${p.id}`} onClick={() => action(p.id, "fail")} className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700">Fail</button>}
                      {p.status === "FAILED" && <button data-testid={`payout-retry-${p.id}`} onClick={() => action(p.id, "retry")} className="rounded-md border border-stone-300 px-2 py-1 text-xs">Retry</button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Mark paid dialog */}
      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent data-testid="payout-pay-dialog">
          <DialogHeader><DialogTitle>Mark payout paid — {payDlg?.payout_ref} ({formatINR(payDlg?.net_amount)})</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-stone-500">Payment method</label>
              <select data-testid="pay-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm">
                {["UPI", "NEFT", "IMPS", "Bank Transfer"].map((m) => <option key={m}>{m}</option>)}
              </select></div>
            <div><label className="text-xs text-stone-500">Payout reference</label><Input data-testid="pay-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="UTR / transaction ref" /></div>
            <div><label className="text-xs text-stone-500">Payout date</label><Input data-testid="pay-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="text-xs text-stone-500">Admin note (optional)</label><Input data-testid="pay-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="pay-confirm" onClick={markPaid}>Confirm paid</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold dialog */}
      <Dialog open={!!holdDlg} onOpenChange={(o) => !o && setHoldDlg(null)}>
        <DialogContent data-testid="payout-hold-dialog">
          <DialogHeader><DialogTitle>Place payout on hold — {holdDlg?.payout_ref}</DialogTitle></DialogHeader>
          <div><label className="text-xs text-stone-500">Reason</label><Input data-testid="hold-reason" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder="e.g. Customer dispute, refund pending" /></div>
          <DialogFooter><Button data-testid="hold-confirm" onClick={doHold}>Place on hold</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
